/**
 * Passive observation for the cloud runtime named by the current route.
 *
 * Why: saved cloud environments are deliberately NOT auto-connected on startup
 * (that would fan out to every VM — the 429 amplifier + VM-cost concern; see
 * runtime/service.threadSubscriptions "does not auto-connect saved environments
 * on startup"). Cloud thread and draft routes retain detail state on mount but
 * do not otherwise connect the runtime; the connect only fired from sidebar
 * actions. A fresh load may show durable history before the runtime connects.
 * Execution actions connect through the shared Execution Intent module.
 */
import { useMemo } from "react";
import { isAutoConnectableRuntimeStatus } from "../cmmd/forgeRuntimeStatus";
import type { SidebarPreConnectCloudMetadata } from "../components/sidebar/SidebarProjectItem";
import {
  useCloudRuntimeProjectHandoffStore,
  type CloudRuntimeProjectHandoff,
} from "../components/sidebar/cloudRuntimeProjectHandoffStore";
import type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
} from "../cmmd/cmmdForgeRuntimeListResource";
import type { CloudEnvironmentLifecycleState } from "../environments/primary/cloudEnvironmentLifecycleProjection";
import { requiresForgeServerReconciliation } from "../environments/primary/cloudProjectCatalogContracts";
import {
  createCloudRuntimeMetadata,
  findCloudRowForEnvironment,
  isStoppedByOperator,
} from "./cloudRouteCatalogRow";
import { useCmmdForgeRuntimeListSnapshot } from "../cmmd/cmmdForgeRuntimeListResource";
import {
  useIsCloudEnvironmentConnected,
  useRefreshCloudRuntimeStatus,
} from "./activeRouteCloudRuntimeObservation";
export { shouldRefreshRuntimeStatus } from "./activeRouteCloudRuntimeObservation";

export function findCloudEnvironmentLifecycleState(
  environmentId: string | null | undefined,
  environments: readonly CloudEnvironmentCatalogRow[] | undefined,
): CloudEnvironmentLifecycleState | undefined {
  if (!environmentId || !environments) return undefined;
  return environments.find(
    (environment) =>
      environment.environmentId === environmentId ||
      environment.cmmdEnvironmentId === environmentId,
  )?.lifecycle?.state;
}

/**
 * How long a cloud runtime's reconciled status is trusted before we ask the
 * server again. Long enough that navigating around inside one runtime does not
 * re-request on every route change, short enough that a machine stopped or
 * destroyed out from under the UI stops reading as `running` almost
 * immediately rather than waiting for the 5-minute background reconciler.
 */
/**
 * Decide whether the active route's environment is a cloud runtime that needs
 * connecting, and if so return the metadata to connect it with. Pure so the
 * bug scenario (disconnected cloud env + catalog row available) is unit-tested
 * without React/websocket plumbing.
 *
 * Returns null (do nothing) when: not a cloud runtime env, already connected,
 * or the catalog row isn't loaded yet (we never fabricate connect metadata).
 */
export function resolveCloudRuntimeToConnect(input: {
  readonly environmentId: string | null | undefined;
  readonly isConnected: boolean;
  readonly catalogRows: readonly CloudProjectCatalogRow[];
  readonly handoffs?: readonly CloudRuntimeProjectHandoff[];
  readonly nowMs?: number;
  readonly lifecycleState?: CloudEnvironmentLifecycleState | undefined;
  /**
   * Only an explicit execution action may connect a Sleeping Environment.
   * Route and history reads keep the default false.
   */
  readonly allowSleepingEnvironment?: boolean;
}): SidebarPreConnectCloudMetadata | null {
  if (input.lifecycleState === "paused" || input.lifecycleState === "unavailable") return null;
  if (input.isConnected) return null;
  const resolved = findCloudRowForEnvironment(
    input.environmentId,
    input.catalogRows,
    input.handoffs ?? [],
    input.nowMs,
  );
  if (!resolved) return null;
  // A runtime on an older Forge build may still have a healthy socket, but it
  // must not accept another turn. Connecting it would execute against code the
  // control plane has already declared stale and can reproduce fixed auth bugs.
  if (!resolved.matchedByHandoff && requiresForgeServerReconciliation(resolved.row.maintenance)) {
    return null;
  }
  // Gate the auto-wake: a registered-but-unprovisioned, stopped/hibernated, or
  // errored runtime must not be silently started on open — that provisions or
  // wakes a machine (cost) and blocks while it boots. The user starts it
  // deliberately through an explicit execution action.
  if (
    !input.allowSleepingEnvironment &&
    !resolved.matchedByHandoff &&
    !isAutoConnectableRuntimeStatus(resolved.row.status)
  ) {
    return null;
  }
  // An explicit operator stop outranks a connectable-looking status. Connect is
  // admission, not lifecycle authority: asking anyway earns a hard 409 and robs
  // the user of the Start affordance that would actually work.
  if (!resolved.matchedByHandoff && isStoppedByOperator(resolved.row)) return null;
  return createCloudRuntimeMetadata(resolved.row, resolved.runtimeId);
}

interface CloudRuntimeRouteBlockBase {
  readonly meta: SidebarPreConnectCloudMetadata;
}

export type CloudRuntimeRouteBlockState =
  /**
   * Registered but never provisioned. Actionable and NOT a failure: the user is
   * offered "Start project", which provisions for the first time. Kept
   * distinct from `sleeping` so the UI does not claim there is a hibernated
   * machine to wake, and from `error` so a brand-new project never looks broken.
   */
  | (CloudRuntimeRouteBlockBase & { readonly kind: "unprovisioned"; readonly status: "registered" })
  | (CloudRuntimeRouteBlockBase & { readonly kind: "sleeping"; readonly status: "stopped" })
  | (CloudRuntimeRouteBlockBase & { readonly kind: "paused"; readonly status: "stopped" })
  | (CloudRuntimeRouteBlockBase & { readonly kind: "error"; readonly status: "error" })
  | (CloudRuntimeRouteBlockBase & { readonly kind: "draining"; readonly status: "draining" })
  | (CloudRuntimeRouteBlockBase & {
      readonly kind: "update-required";
      readonly status: "running" | "suspended" | "stopped";
    })
  | (CloudRuntimeRouteBlockBase & { readonly kind: "destroyed"; readonly status: "destroyed" });

export type CloudRuntimeRouteBlockKind = CloudRuntimeRouteBlockState["kind"];

/**
 * Resolve every canonical runtime state that blocks route attachment. This is
 * intentionally broader than a wake affordance: draining is observable but not
 * actionable, while destroyed runtimes can be recreated deliberately.
 */
export function resolveCloudRuntimeRouteBlockState(input: {
  readonly environmentId: string | null | undefined;
  readonly isConnected: boolean;
  readonly catalogRows: readonly CloudProjectCatalogRow[];
  readonly handoffs?: readonly CloudRuntimeProjectHandoff[];
  readonly nowMs?: number;
}): CloudRuntimeRouteBlockState | null {
  const resolved = findCloudRowForEnvironment(
    input.environmentId,
    input.catalogRows,
    input.handoffs ?? [],
    input.nowMs,
  );
  if (!resolved || resolved.matchedByHandoff) return null;
  const { row } = resolved;
  // Staleness outranks connection state. An already-open websocket proves only
  // that the old daemon is reachable, not that it is safe to execute a turn.
  if (
    requiresForgeServerReconciliation(row.maintenance) &&
    (row.status === "running" || row.status === "suspended" || row.status === "stopped")
  ) {
    return {
      meta: createCloudRuntimeMetadata(row),
      status: row.status,
      kind: "update-required",
    };
  }
  if (input.isConnected) return null;
  // Checked BEFORE the status gate: a runtime the owner stopped can be sitting
  // at `suspended` (its sprite went warm and the reconciler adopted that), which
  // the status gate would wave through as "nothing blocking" — leaving the route
  // with no panel and no explanation for the 409 the connect just took.
  if (isStoppedByOperator(row)) {
    return { meta: createCloudRuntimeMetadata(row), status: "stopped", kind: "paused" };
  }
  if (isAutoConnectableRuntimeStatus(row.status)) return null;

  const meta = createCloudRuntimeMetadata(row);
  switch (row.status) {
    case "registered":
      return { meta, status: row.status, kind: "unprovisioned" };
    case "stopped":
      return { meta, status: row.status, kind: "sleeping" };
    case "error":
      return { meta, status: row.status, kind: "error" };
    case "draining":
      return { meta, status: row.status, kind: "draining" };
    case "destroyed":
      return { meta, status: row.status, kind: "destroyed" };
    case "running":
    case "starting":
    case "provisioning":
    // Unreachable in practice — the isAutoConnectableRuntimeStatus guard above
    // already returned null for `suspended`. Listed anyway because TypeScript
    // cannot narrow through that helper, and because the answer is the same
    // either way: a suspended sprite does not BLOCK route attachment, it just
    // needs the connect that the guard lets through.
    case "suspended":
      return null;
    default: {
      const _exhaustive: never = row.status;
      void _exhaustive;
      return null;
    }
  }
}

/**
 * Effect hook: when the route's environment is a not-yet-connected cloud
 * runtime, connect it once (retry allowed if the connect fails). A no-op for
 * local/primary environments and for already-connected cloud runtimes.
 */
export type ActiveRouteCloudRuntimeConnectionState =
  | { readonly kind: "idle" }
  | { readonly kind: "connected" };

export interface ActiveRouteCloudRuntimeConnectionController {
  readonly state: ActiveRouteCloudRuntimeConnectionState;
}

export function useObserveActiveRouteCloudRuntimeConnection(
  environmentId: string | null | undefined,
): ActiveRouteCloudRuntimeConnectionController {
  const snapshot = useCmmdForgeRuntimeListSnapshot();
  const handoffsByRuntimeId = useCloudRuntimeProjectHandoffStore((state) => state.byRuntimeId);
  const handoffs = useMemo(() => Object.values(handoffsByRuntimeId), [handoffsByRuntimeId]);
  const isConnected = useIsCloudEnvironmentConnected(environmentId);
  const observedRuntimeId = useMemo(
    () => findCloudRowForEnvironment(environmentId, snapshot.rows, handoffs)?.row.runtimeId ?? null,
    [environmentId, handoffs, snapshot.rows],
  );
  useRefreshCloudRuntimeStatus(observedRuntimeId);
  return { state: isConnected ? { kind: "connected" } : { kind: "idle" } };
}

/** Reactive canonical block state for the active cloud route. */
export function useCloudRuntimeRouteBlockState(
  environmentId: string | null | undefined,
): CloudRuntimeRouteBlockState | null {
  const snapshot = useCmmdForgeRuntimeListSnapshot();
  const handoffsByRuntimeId = useCloudRuntimeProjectHandoffStore((state) => state.byRuntimeId);
  const handoffs = useMemo(() => Object.values(handoffsByRuntimeId), [handoffsByRuntimeId]);
  const isConnected = useIsCloudEnvironmentConnected(environmentId);
  return useMemo(
    () =>
      resolveCloudRuntimeRouteBlockState({
        environmentId,
        isConnected,
        catalogRows: snapshot.rows,
        handoffs,
      }),
    [environmentId, handoffs, isConnected, snapshot.rows],
  );
}
